/**
 * Cumplimiento (sección 15): tres colas con sus plazos — DSA (avisos de contenido ilegal y
 * solicitudes de revisión), GPSR (órdenes de autoridad y alertas Safety Gate, SLA 2 días) y DAC7
 * (vendedores por encima del umbral y estado de sus datos fiscales; XML en la 2.1).
 */
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import type { Dac7SummaryOutput } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { env } from '../env.js';
import { db } from '../firebase.js';
import { btn, card, field, money, muted, Notice, str, Table, td, when } from '../lib/ui.js';

type Doc = Record<string, unknown>;
type Tab = 'dsa' | 'gpsr' | 'dac7';
const KINDS: Record<Tab, string[]> = {
  dsa: ['dsa_notice'],
  gpsr: ['gpsr_authority_order', 'safety_gate_alert'],
  dac7: ['dac7_report'],
};
const STATUSES = ['open', 'in_review', 'actioned', 'rejected', 'closed'] as const;

function overdue(v: unknown): boolean {
  const d =
    v && typeof v === 'object' && 'toMillis' in v
      ? (v as { toMillis: () => number }).toMillis()
      : 0;
  return d > 0 && d < Date.now();
}

function Cases({ tab }: { tab: Tab }): ReactElement {
  const [status, setStatus] = useState<string>('open');
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [newDesc, setNewDesc] = useState('');
  const [newProduct, setNewProduct] = useState('');
  const [newKind, setNewKind] = useState<'gpsr_authority_order' | 'safety_gate_alert'>(
    'gpsr_authority_order',
  );
  useEffect(() => {
    setRows(null);
    Promise.all(
      KINDS[tab].map((k) =>
        getDocs(
          query(
            collection(db, 'complianceCases'),
            where('kind', '==', k),
            where('status', '==', status),
            orderBy('respond_by', 'asc'),
            limit(100),
          ),
        ),
      ),
    )
      .then((snaps) => {
        setRows(snaps.flatMap((s) => s.docs.map((d) => ({ id: d.id, ...d.data() }))));
      })
      .catch((e: unknown) => {
        setError(errorText(e));
        setRows([]);
      });
  }, [tab, status, version]);
  const resolve = (
    id: string,
    st: 'in_review' | 'actioned' | 'rejected' | 'closed',
    remove = false,
  ): void => {
    const note =
      st === 'in_review'
        ? ''
        : (window.prompt('Nota de resolución (al denunciante / vendedor):') ?? '');
    if (st !== 'in_review' && note === '' && !window.confirm('¿Sin nota?')) return;
    call(api.compliance.resolveCase, {
      id,
      status: st,
      ...(note ? { resolution_note: note } : {}),
      ...(remove ? { remove_listing: true } : {}),
    })
      .then(() => {
        setNotice('Hecho.');
        setVersion((v) => v + 1);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  const create = (): void => {
    if (!newDesc.trim()) return;
    call(api.compliance.createCase, {
      kind: newKind,
      description: newDesc.trim(),
      ...(newProduct.trim() ? { product_id: newProduct.trim() } : {}),
    })
      .then(() => {
        setNewDesc('');
        setNewProduct('');
        setNotice('Caso creado (SLA 2 días).');
        setVersion((v) => v + 1);
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  return (
    <>
      <div style={{ display: 'flex', gap: 8 }}>
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            style={btn(status === s)}
            onClick={() => {
              setStatus(s);
            }}
          >
            {s}
          </button>
        ))}
      </div>
      <Notice text={notice} />
      <Notice text={error} error />
      {tab === 'gpsr' && (
        <div style={{ ...card, display: 'grid', gap: 6, maxWidth: 640 }}>
          <strong>Nueva orden de autoridad / alerta Safety Gate</strong>
          <select
            style={field}
            value={newKind}
            onChange={(e) => {
              setNewKind(e.target.value as typeof newKind);
            }}
          >
            <option value="gpsr_authority_order">Orden de autoridad (retirada)</option>
            <option value="safety_gate_alert">Alerta Safety Gate</option>
          </select>
          <input
            style={field}
            placeholder="id del producto (opcional)"
            value={newProduct}
            onChange={(e) => {
              setNewProduct(e.target.value);
            }}
          />
          <textarea
            style={{ ...field, minHeight: 70 }}
            placeholder="Descripción / referencia de la orden"
            value={newDesc}
            onChange={(e) => {
              setNewDesc(e.target.value);
            }}
          />
          <button type="button" style={{ ...btn(true), justifySelf: 'start' }} onClick={create}>
            Crear caso
          </button>
        </div>
      )}
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && (
        <Table head={['Ref', 'Tipo', 'Objeto', 'Denunciante', 'Descripción', 'Plazo', 'Acciones']}>
          {rows.map((c) => {
            const subject = (c.subject as Doc | undefined) ?? {};
            const reporter = c.reporter as Doc | null | undefined;
            return (
              <tr
                key={str(c.id)}
                style={
                  overdue(c.respond_by) && ['open', 'in_review'].includes(str(c.status))
                    ? { background: '#FEF2F2' }
                    : undefined
                }
              >
                <td style={td}>{str(c.ref)}</td>
                <td style={td}>{str(c.kind)}</td>
                <td style={td}>
                  {subject.type === 'product' ? (
                    <a
                      href={`${env.VITE_WEB_ORIGIN}/es/p/${str(subject.product_id)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {str(subject.product_id)}
                    </a>
                  ) : subject.type === 'user' ? (
                    <Link to={`/usuarios/${str(subject.uid)}`}>{str(subject.uid)}</Link>
                  ) : (
                    `año ${str(subject.year)}`
                  )}
                  {typeof c.location === 'string' && c.location ? (
                    <div style={{ ...muted, fontSize: 11 }}>
                      <a href={c.location} target="_blank" rel="noreferrer">
                        {c.location.slice(0, 60)}
                      </a>
                    </div>
                  ) : null}
                </td>
                <td style={td}>
                  {reporter
                    ? `${str(reporter.name)} ${str(reporter.email)}`.trim() || str(reporter.uid)
                    : '—'}
                  {reporter?.is_authority === true ? ' (autoridad)' : ''}
                </td>
                <td style={{ ...td, maxWidth: 320, whiteSpace: 'pre-wrap' }}>
                  {str(c.description).slice(0, 400)}
                </td>
                <td style={td}>{when(c.respond_by)}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {c.status === 'open' && (
                    <button
                      type="button"
                      style={btn(false)}
                      onClick={() => {
                        resolve(str(c.id), 'in_review');
                      }}
                    >
                      En revisión
                    </button>
                  )}{' '}
                  {['open', 'in_review'].includes(str(c.status)) && (
                    <>
                      {subject.type === 'product' && (
                        <button
                          type="button"
                          style={btn(false, true)}
                          onClick={() => {
                            resolve(str(c.id), 'actioned', true);
                          }}
                        >
                          Retirar anuncio
                        </button>
                      )}{' '}
                      <button
                        type="button"
                        style={btn(true)}
                        onClick={() => {
                          resolve(str(c.id), 'actioned');
                        }}
                      >
                        Accionado
                      </button>{' '}
                      <button
                        type="button"
                        style={btn(false)}
                        onClick={() => {
                          resolve(str(c.id), 'rejected');
                        }}
                      >
                        Rechazar
                      </button>
                    </>
                  )}{' '}
                  {c.status !== 'closed' && (
                    <button
                      type="button"
                      style={btn(false)}
                      onClick={() => {
                        resolve(str(c.id), 'closed');
                      }}
                    >
                      Cerrar
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td style={td} colSpan={7}>
                Nada en esta cola.
              </td>
            </tr>
          )}
        </Table>
      )}
    </>
  );
}

function Dac7(): ReactElement {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState<Dac7SummaryOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = (): void => {
    setBusy(true);
    setError(null);
    call(api.compliance.dac7Summary, { year })
      .then(setData)
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <>
      <p style={muted}>
        DAC7 exime a vendedores con menos de 30 ventas y menos de 2.000 € al año; nuvvu pide los
        datos fiscales al llegar a 25 ventas o 1.800 €. Jurisdicción de reporte: España, antes del
        31 de enero. La exportación XML (DPI) llega en la 2.1; aquí se ve quién es reportable y si
        ha aportado sus datos.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          style={{ ...field, width: 120 }}
          type="number"
          value={year}
          onChange={(e) => {
            setYear(Number(e.target.value));
          }}
        />
        <button type="button" style={btn(true)} disabled={busy} onClick={run}>
          Calcular
        </button>
      </div>
      <Notice text={error} error />
      {data && (
        <Table
          head={[
            'Vendedor',
            'País',
            'Ventas',
            'Importes',
            'Total EUR',
            'Reportable',
            'Datos fiscales',
          ]}
        >
          {data.sellers.map((s) => (
            <tr
              key={s.uid}
              style={
                s.reportable && s.tax_profile_status !== 'provided'
                  ? { background: '#FEF2F2' }
                  : undefined
              }
            >
              <td style={td}>
                <Link to={`/usuarios/${s.uid}`}>@{s.username ?? s.uid.slice(-6)}</Link>
              </td>
              <td style={td}>{s.country_code ?? ''}</td>
              <td style={td}>{s.sales}</td>
              <td style={td}>{s.amounts.map((a) => money(a)).join(' · ')}</td>
              <td style={td}>{money({ amount: s.amount_eur, currency: 'EUR' })}</td>
              <td style={td}>{s.reportable ? 'sí' : 'no'}</td>
              <td style={td}>{s.tax_profile_status}</td>
            </tr>
          ))}
          {data.sellers.length === 0 && (
            <tr>
              <td style={td} colSpan={7}>
                Sin ventas completadas en {data.year}.
              </td>
            </tr>
          )}
        </Table>
      )}
    </>
  );
}

export function CompliancePage(): ReactElement {
  const [tab, setTab] = useState<Tab>('dsa');
  return (
    <>
      <h1 style={{ margin: 0 }}>Cumplimiento</h1>
      <p style={muted}>
        DSA: avisos de contenido ilegal (art. 16) y solicitudes de revisión de decisiones (art. 20);
        respuesta motivada en 7 días, con email al denunciante. GPSR: órdenes de autoridad y Safety
        Gate, 2 días hábiles. DAC7: informe anual. Infinity Advance SL es microempresa (L1): exenta
        de los arts. 19/29 y del informe de transparencia.
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        {(['dsa', 'gpsr', 'dac7'] as const).map((k) => (
          <button
            key={k}
            type="button"
            style={btn(tab === k)}
            onClick={() => {
              setTab(k);
            }}
          >
            {k.toUpperCase()}
          </button>
        ))}
      </div>
      {tab === 'dac7' ? <Dac7 /> : <Cases tab={tab} />}
    </>
  );
}
